/**
 * The post-processing stack.
 *
 * VISUAL_REALISM_AUDIT R-01: the single largest reason the build read as a
 * stylised toy was that it had no ambient occlusion. Every crevice in the world
 * — the underside of a porch, the inside of the eaves, where a fence post
 * enters the ground, the gap between two parked cars — was lit exactly as
 * brightly as an open field. Real geometry darkens where it occludes itself,
 * and the absence of that reads as "computer graphics" below conscious level.
 *
 * Why ground-truth AO rather than the alternatives:
 *
 *   - **Baked vertex AO** would be free per frame and the world is static, so
 *     on paper it is the ideal fit. It is not viable here: the world is built
 *     from boxes, so a merged wall has eight vertices and there is nothing to
 *     interpolate an occlusion term across. Low-poly geometry and vertex AO are
 *     mutually exclusive.
 *   - **SSAOPass** is cheaper but haloes visibly around silhouettes, which on a
 *     scene of hard-edged boxes is exactly where the eye is already looking.
 *   - **More contact-shadow decals** — what the build already did — cannot
 *     reach a porch ceiling or a doorway recess at all.
 *
 * So: `GTAOPass`, from `three/examples/jsm`, which is already in the dependency
 * tree. No new package and no new licence (three is MIT and already registered).
 *
 * The whole stack is optional. `quality: 'low'` renders straight to the screen
 * with no composer at all, so the effects can never be the reason someone
 * cannot run the game.
 */
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';

export type RenderQuality = 'low' | 'medium' | 'high';

/** What one frame cost, split so each half can be judged on its own terms. */
export interface RenderStats {
  /**
   * Draw calls and triangles for the world itself: the scene render and its
   * shadow pass. This is the number that grows when the world gets more
   * complicated, so this is the one worth gating.
   */
  sceneCalls: number;
  sceneTriangles: number;
  /**
   * The whole frame including post-processing. Ambient occlusion needs a
   * depth-normal prepass, which is a second pass over the scene, so this runs
   * at roughly twice the scene figure by design. It is reported rather than
   * gated: it is a bounded, deliberate, quality-gated cost, not creeping
   * complexity.
   */
  frameCalls: number;
  frameTriangles: number;
}

export interface RenderPipeline {
  /** Draw one frame at whatever quality is currently set. */
  render(): void;
  /** Cost of the most recent frame. */
  readonly stats: RenderStats;
  setQuality(quality: RenderQuality): void;
  setSize(width: number, height: number): void;
  dispose(): void;
  /** True when the composer is doing the drawing rather than the renderer. */
  readonly composited: boolean;
}

export function createPipeline(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
  options: {
    /**
     * Never build a composer at all.
     *
     * Set on phones. Ambient occlusion wants float render targets and a long
     * shader, which is the most likely thing in the whole build to be refused
     * by a mobile driver - and a refusal used to mean a blank screen rather
     * than a plainer one.
     */
    disabled?: boolean;
  } = {},
): RenderPipeline {
  let quality: RenderQuality = 'high';
  /** Set once a driver has refused the composer, so it is not retried. */
  let failed = false;
  let composer: EffectComposer | null = null;
  let gtao: GTAOPass | null = null;
  let width = Math.max(1, renderer.domElement.width);
  let height = Math.max(1, renderer.domElement.height);
  const stats: RenderStats = {
    sceneCalls: 0, sceneTriangles: 0, frameCalls: 0, frameTriangles: 0,
  };

  /**
   * A pass that draws nothing and reads the counters.
   *
   * EffectComposer has no per-pass hook, so the only way to know what the scene
   * alone cost is to sit immediately after the RenderPass and look.
   */
  const probe = {
    enabled: true,
    needsSwap: false,
    clear: false,
    renderToScreen: false,
    setSize(): void {},
    dispose(): void {},
    render(): void {
      stats.sceneCalls = renderer.info.render.calls;
      stats.sceneTriangles = renderer.info.render.triangles;
    },
  } as unknown as RenderPass;

  function build(): void {
    teardown();
    if (quality === 'low' || options.disabled) return;

    const made = new EffectComposer(renderer);
    made.addPass(new RenderPass(scene, camera));
    made.addPass(probe);

    // Resolution of the AO buffer. Half-res on medium: AO is a low-frequency
    // term and the denoise pass hides the difference, but it is a large saving
    // on a weak GPU.
    const scale = quality === 'high' ? 1 : 0.5;
    const pass = new GTAOPass(
      scene, camera, Math.round(width * scale), Math.round(height * scale),
    );

    // Tuned to this world's scale, which is metres. A 0.9 m radius reaches into
    // a doorway reveal and under a porch without darkening whole walls, which
    // is what a larger radius does to geometry made of big flat boxes.
    pass.updateGtaoMaterial({
      radius: 0.9,
      distanceExponent: 1.4,
      thickness: 1.2,
      scale: 1.1,
      samples: quality === 'high' ? 16 : 8,
      distanceFallOff: 1,
    });
    // Held well below 1: full-strength AO on a stylised palette reads as soot
    // in the corners rather than as shading.
    pass.blendIntensity = 0.62;
    made.addPass(pass);

    // Tone mapping and colour space conversion move here once a composer is in
    // play; without it the frame is rendered twice into sRGB and washes out.
    made.addPass(new OutputPass());
    made.setSize(width, height);

    composer = made;
    gtao = pass;
  }

  /**
   * Build the composer, and fall back to a plain render if anything refuses.
   *
   * The catch is the point. A driver that rejects the ambient-occlusion pass
   * should cost the player some shading, not the whole game: before this, a
   * throw here propagated out of startup and left the loading bar on screen
   * forever.
   */
  function buildSafely(): void {
    try {
      build();
    } catch (error) {
      console.warn('[LIFE EMPIRE] post-processing unavailable; rendering directly', error);
      teardown();
      failed = true;
    }
  }

  function teardown(): void {
    gtao?.dispose();
    composer?.dispose();
    gtao = null;
    composer = null;
  }

  return {
    get composited(): boolean {
      return composer !== null;
    },
    stats,
    render(): void {
      // `renderer.info` resets itself at the start of every `render()` call by
      // default. With a composer that means the counters end the frame holding
      // only the last full-screen pass - one draw call, two triangles - and the
      // draw-call and triangle budgets in tools/perf.mjs silently start
      // measuring nothing. Resetting once per frame by hand makes them count
      // the whole frame instead: the scene plus every post-processing pass,
      // which is the number that actually matters.
      renderer.info.autoReset = false;
      renderer.info.reset();
      if (composer) {
        composer.render();
      } else {
        renderer.render(scene, camera);
        stats.sceneCalls = renderer.info.render.calls;
        stats.sceneTriangles = renderer.info.render.triangles;
      }
      stats.frameCalls = renderer.info.render.calls;
      stats.frameTriangles = renderer.info.render.triangles;
    },
    setQuality(next: RenderQuality): void {
      // Once a driver has refused the composer, stop asking. Retrying on every
      // settings change would throw on every settings change.
      if (failed || options.disabled) { quality = next; return; }
      if (next === quality && (composer !== null) === (next !== 'low')) return;
      quality = next;
      buildSafely();
    },
    setSize(w: number, h: number): void {
      width = Math.max(1, w);
      height = Math.max(1, h);
      composer?.setSize(width, height);
      gtao?.setSize(
        Math.round(width * (quality === 'high' ? 1 : 0.5)),
        Math.round(height * (quality === 'high' ? 1 : 0.5)),
      );
    },
    dispose: teardown,
  };
}
